import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import DatePicker from '../../components/DatePicker';
import { apiRequest } from '../../services/api';
import UsageInfo from '../../components/UsageInfo';
import { downloadExcel, printTable } from '../../utils/exportUtils';
import LoadingSpinner from '../../components/LoadingSpinner';
import Pagination from '../../components/Pagination';

// ── Yardımcı Fonksiyonlar ──────────────────────────────────────────────────────
function tcDogrula(tc) {
  if (!tc || !tc.trim()) return null;
  tc = tc.trim();
  if (tc.length !== 11) return 'T.C. kimlik numarası 11 haneli olmalıdır.';
  if (!/^\d+$/.test(tc)) return 'Yalnızca rakamlardan oluşmalıdır.';
  if (tc[0] === '0') return 'İlk hane 0 olamaz.';
  if (tc === tc[0].repeat(11)) return 'Geçersiz T.C. kimlik numarası.';
  const d = tc.split('').map(Number);
  const d10 = ((7 * (d[0] + d[2] + d[4] + d[6] + d[8]) - (d[1] + d[3] + d[5] + d[7])) % 10 + 10) % 10;
  if (d[9] !== d10) return 'Geçersiz T.C. (kontrol hanesi hatalı).';
  const d11 = d.slice(0, 10).reduce((a, b) => a + b, 0) % 10;
  if (d[10] !== d11) return 'Geçersiz T.C. (kontrol hanesi hatalı).';
  return null;
}
function pasaportDogrula(p) {
  if (!p || !p.trim()) return null;
  p = p.trim();
  if (p.length < 6 || p.length > 15) return 'Pasaport numarası 6-15 karakter olmalıdır.';
  if (!/^[A-Za-z0-9]+$/.test(p)) return 'Yalnızca harf ve rakamlardan oluşmalıdır.';
  return null;
}
function parseDate(str) {
  if (!str) return null;
  const [g, a, y] = str.split('.').map(Number);
  return new Date(y, a - 1, g);
}
function fmtTarih(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}
function yasHesapla(dogumTarihi) {
  if (!dogumTarihi) return null;
  const dogum = dogumTarihi.includes('.') ? parseDate(dogumTarihi) : new Date(dogumTarihi);
  if (!dogum || isNaN(dogum.getTime())) return null;
  const bugun = new Date();
  let yas = bugun.getFullYear() - dogum.getFullYear();
  const ay = bugun.getMonth() - dogum.getMonth();
  if (ay < 0 || (ay === 0 && bugun.getDate() < dogum.getDate())) yas--;
  return yas;
}

// ── Ortak Stiller ─────────────────────────────────────────────────────────────
const INP = (err) => `w-full border-2 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-800 transition-all bg-white focus:outline-none ${err ? 'border-rose-400 bg-rose-50' : 'border-gray-100 focus:border-indigo-400 shadow-sm'}`;
const LABEL = "text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1";
const MODAL_OVERLAY = "fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[300] flex items-center justify-center p-4 animate-fade-in";
const MODAL_INNER = "bg-white w-full rounded-[2.5rem] shadow-2xl border border-white/20 overflow-hidden animate-modal-in flex flex-col";

// ── Avatar ────────────────────────────────────────────────────────────────────
function InitialsAvatar({ name, size = 'md' }) {
  const parts = (name || '').split(' ').filter(Boolean);
  const initials = parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : (parts[0]?.[0] || '?');
  const colors = ['bg-violet-500', 'bg-indigo-500', 'bg-sky-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500'];
  const idx = (name?.charCodeAt(0) || 0) % colors.length;
  const sz = size === 'lg' ? 'w-12 h-12 text-base' : 'w-9 h-9 text-xs';
  return (
    <span className={`inline-flex items-center justify-center ${sz} rounded-2xl text-white font-black flex-shrink-0 shadow-lg ${colors[idx]}`}>
      {initials.toUpperCase()}
    </span>
  );
}

// ── Hata Mesajı ───────────────────────────────────────────────────────────────
function FormHata({ mesaj }) {
  if (!mesaj) return null;
  return (
    <div className="mt-2 bg-rose-50 border border-rose-200 rounded-xl px-4 py-2.5 flex items-center gap-2">
      <span className="text-rose-500 text-xs">⚠</span>
      <p className="text-[11px] font-black text-rose-600 uppercase tracking-widest">{mesaj}</p>
    </div>
  );
}

// ── Misafir Ekle / Düzenle Modal ──────────────────────────────────────────────
function MisafirDuzenleModal({ misafir, onClose, onKaydedildi, yeniKayit = false }) {
  const [form, setForm] = useState({
    adSoyad: misafir.adSoyad || '',
    tcPasaportNo: misafir.tcPasaportNo || '',
    pasaportNo: misafir.pasaportNo || '',
    pasaportGecerlilikTarihi: misafir.pasaportGecerlilikTarihi ? fmtTarih(misafir.pasaportGecerlilikTarihi) : '',
    uyruk: misafir.uyruk || '',
    telefon: misafir.telefon || '',
    email: misafir.email || '',
    ulke: misafir.ulke || 'TÜRKİYE',
    cinsiyet: misafir.cinsiyet || 'Erkek',
    dogumTarihi: misafir.dogumTarihi ? fmtTarih(misafir.dogumTarihi) : '',
    dogumYeri: misafir.dogumYeri || '',
    meslek: misafir.meslek || '',
    adres: misafir.adres || '',
    anaAdi: misafir.anaAdi || '',
    babaAdi: misafir.babaAdi || '',
    aracPlaka: misafir.aracPlaka || '',
    firma: misafir.firma || '',
  });
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [kimlikHata, setKimlikHata] = useState(null);
  const set = (key, val) => { setForm(p => ({ ...p, [key]: val })); setKimlikHata(null); };

  async function handleKaydet() {
    if (!form.adSoyad.trim()) { setKimlikHata('Ad Soyad zorunludur.'); return; }
    const turkiye = form.ulke === 'TÜRKİYE';
    if (turkiye) {
      if (!form.tcPasaportNo) { setKimlikHata('T.C. kimlik numarası zorunludur.'); return; }
      const hata = tcDogrula(form.tcPasaportNo);
      if (hata) { setKimlikHata(hata); return; }
    } else {
      if (!form.pasaportNo) { setKimlikHata('Pasaport numarası zorunludur.'); return; }
      const hata = pasaportDogrula(form.pasaportNo);
      if (hata) { setKimlikHata(hata); return; }
    }
    setKimlikHata(null);
    setKaydediliyor(true);
    try {
      const parseTarih = (str) => {
        if (!str) return null;
        const [g, a, y] = str.split('.').map(Number);
        return new Date(Date.UTC(y, a - 1, g)).toISOString();
      };
      const payload = {
        ...misafir, ...form,
        tcPasaportNo: form.ulke === 'TÜRKİYE' ? form.tcPasaportNo : null,
        pasaportNo: form.ulke !== 'TÜRKİYE' ? form.pasaportNo : null,
        pasaportGecerlilikTarihi: parseTarih(form.pasaportGecerlilikTarihi),
        dogumTarihi: parseTarih(form.dogumTarihi),
      };
      if (yeniKayit) await apiRequest('misafirler', 'POST', payload);
      else await apiRequest('misafirler', 'PUT', payload);
      onKaydedildi?.();
      onClose();
    } catch (err) {
      setKimlikHata(err.message || 'Kayıt başarısız.');
    }
    setKaydediliyor(false);
  }

  const turkiye = form.ulke === 'TÜRKİYE';

  return createPortal(
    <div className={MODAL_OVERLAY} onClick={onClose}>
      <div className={`${MODAL_INNER} max-w-2xl max-h-[92vh]`} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="relative flex items-center justify-between px-8 py-7 bg-gradient-to-r from-indigo-600 to-violet-600 overflow-hidden shrink-0">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-56 h-56 bg-white/10 rounded-full blur-3xl pointer-events-none" />
          <div className="flex items-center gap-4 relative">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-2xl shadow-inner border border-white/30">
              {yeniKayit ? '➕' : '✏️'}
            </div>
            <div>
              <h2 className="font-black text-xl tracking-tight text-white uppercase leading-none">
                {yeniKayit ? 'Yeni Misafir Kaydı' : 'Misafir Düzenle'}
              </h2>
              {!yeniKayit && <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-1.5">{misafir.adSoyad}</p>}
            </div>
          </div>
          <button onClick={onClose} className="relative w-10 h-10 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all text-xl">✕</button>
        </div>

        {/* Body */}
        <div className="p-8 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

            {/* Ad Soyad */}
            <div className="sm:col-span-2">
              <label className={LABEL}>Ad Soyad *</label>
              <input value={form.adSoyad} onChange={e => set('adSoyad', e.target.value)} className={INP(false)} placeholder="Ad ve Soyad" />
            </div>

            {/* Ülke */}
            <div>
              <label className={LABEL}>Ülke</label>
              <select value={form.ulke}
                onChange={e => setForm(p => ({ ...p, ulke: e.target.value, tcPasaportNo: '', pasaportNo: '', pasaportGecerlilikTarihi: '', uyruk: '' }))}
                className={INP(false) + ' cursor-pointer uppercase'}>
                {['TÜRKİYE','ALMANYA','İNGİLTERE','RUSYA','FRANSA','ABD','HOLLANDA','BELÇİKA','İSVİÇRE','İTALYA','İSPANYA','JAPONYA','ÇİN','UKRAYNA','DİĞER'].map(u => <option key={u}>{u}</option>)}
              </select>
            </div>

            {/* Cinsiyet */}
            <div>
              <label className={LABEL}>Cinsiyet</label>
              <select value={form.cinsiyet} onChange={e => set('cinsiyet', e.target.value)} className={INP(false) + ' cursor-pointer'}>
                <option>Erkek</option><option>Kadın</option>
              </select>
            </div>

            {/* Kimlik */}
            {turkiye ? (
              <div className="sm:col-span-2">
                <label className={LABEL}>T.C. Kimlik No *</label>
                <input value={form.tcPasaportNo} onChange={e => { set('tcPasaportNo', e.target.value); }}
                  className={INP(form.tcPasaportNo && tcDogrula(form.tcPasaportNo))}
                  maxLength={11} placeholder="11 haneli T.C. kimlik numarası" />
                {form.tcPasaportNo && tcDogrula(form.tcPasaportNo) && (
                  <FormHata mesaj={tcDogrula(form.tcPasaportNo)} />
                )}
              </div>
            ) : (
              <>
                <div>
                  <label className={LABEL}>Pasaport No *</label>
                  <input value={form.pasaportNo} onChange={e => set('pasaportNo', e.target.value)}
                    className={INP(form.pasaportNo && pasaportDogrula(form.pasaportNo))} />
                  {form.pasaportNo && pasaportDogrula(form.pasaportNo) && <FormHata mesaj={pasaportDogrula(form.pasaportNo)} />}
                </div>
                <div>
                  <label className={LABEL}>Pasaport Geçerlilik</label>
                  <DatePicker value={form.pasaportGecerlilikTarihi} onChange={v => set('pasaportGecerlilikTarihi', v)} className={INP(false)} />
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL}>Uyruk / Milliyet</label>
                  <input value={form.uyruk} onChange={e => set('uyruk', e.target.value)} className={INP(false)} placeholder="Örn: Alman, Rus..." />
                </div>
              </>
            )}

            {/* İletişim */}
            <div>
              <label className={LABEL}>Telefon</label>
              <input value={form.telefon} onChange={e => set('telefon', e.target.value)} className={INP(false)} placeholder="+90 5xx xxx xx xx" />
            </div>
            <div>
              <label className={LABEL}>E-mail</label>
              <input value={form.email} onChange={e => set('email', e.target.value)} className={INP(false)} placeholder="ornek@mail.com" type="email" />
            </div>

            {/* Doğum */}
            <div>
              <label className={LABEL}>Doğum Tarihi</label>
              <DatePicker value={form.dogumTarihi} onChange={v => set('dogumTarihi', v)} className={INP(false)} />
            </div>
            <div>
              <label className={LABEL}>Doğum Yeri</label>
              <input value={form.dogumYeri} onChange={e => set('dogumYeri', e.target.value)} className={INP(false)} />
            </div>
            <div>
              <label className={LABEL}>Ana Adı</label>
              <input value={form.anaAdi} onChange={e => set('anaAdi', e.target.value)} className={INP(false)} />
            </div>
            <div>
              <label className={LABEL}>Baba Adı</label>
              <input value={form.babaAdi} onChange={e => set('babaAdi', e.target.value)} className={INP(false)} />
            </div>
            <div>
              <label className={LABEL}>Araç Plaka</label>
              <input value={form.aracPlaka} onChange={e => set('aracPlaka', e.target.value)} className={INP(false)} />
            </div>

            {/* Diğer */}
            <div>
              <label className={LABEL}>Meslek</label>
              <input value={form.meslek} onChange={e => set('meslek', e.target.value)} className={INP(false)} />
            </div>
            <div>
              <label className={LABEL}>Firma</label>
              <input value={form.firma} onChange={e => set('firma', e.target.value)} className={INP(false)} placeholder="Şirket / Kurum adı (zorunlu değil)" />
            </div>
            <div className="sm:col-span-2">
              <label className={LABEL}>Adres</label>
              <input value={form.adres} onChange={e => set('adres', e.target.value)} className={INP(false)} />
            </div>
          </div>

          {kimlikHata && <FormHata mesaj={kimlikHata} />}
        </div>

        {/* Footer */}
        <div className="px-8 py-6 border-t border-gray-100 bg-gray-50/50 flex gap-3 justify-end shrink-0">
          <button onClick={onClose} className="px-7 py-3.5 rounded-2xl border-2 border-gray-100 text-[10px] font-black text-gray-400 hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95">
            İPTAL
          </button>
          <button onClick={handleKaydet} disabled={kaydediliyor}
            className="px-10 py-3.5 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 disabled:opacity-60 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-indigo-100 transition-all hover:-translate-y-0.5 active:translate-y-0 tracking-widest uppercase">
            {kaydediliyor ? '⌛ KAYDEDİLİYOR...' : yeniKayit ? '✓ MİSAFİRİ KAYDET' : '✓ DEĞİŞİKLİKLERİ KAYDET'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Ülke İstatistik Modal ─────────────────────────────────────────────────────
function UlkeIstatistikModal({ misafirler, onClose }) {
  const istatistik = misafirler.reduce((acc, m) => {
    const ulke = m.ulke || 'Bilinmiyor';
    if (!acc[ulke]) acc[ulke] = { ulke, kisi: 0 };
    acc[ulke].kisi++;
    return acc;
  }, {});
  const liste = Object.values(istatistik).sort((a, b) => b.kisi - a.kisi);
  const toplam = liste.reduce((s, u) => s + u.kisi, 0);
  const maxKisi = liste[0]?.kisi || 1;

  return createPortal(
    <div className={MODAL_OVERLAY} onClick={onClose}>
      <div className={`${MODAL_INNER} max-w-lg max-h-[85vh]`} onClick={e => e.stopPropagation()}>
        <div className="relative flex items-center justify-between px-8 py-7 bg-gradient-to-r from-indigo-600 to-violet-600 overflow-hidden shrink-0">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-56 h-56 bg-white/10 rounded-full blur-3xl pointer-events-none" />
          <div className="flex items-center gap-4 relative">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-2xl shadow-inner border border-white/30">🌍</div>
            <div>
              <h2 className="font-black text-xl tracking-tight text-white uppercase leading-none">Ülke Dağılımı</h2>
              <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-1.5">{toplam} misafir · {liste.length} ülke</p>
            </div>
          </div>
          <button onClick={onClose} className="relative w-10 h-10 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all text-xl">✕</button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-3 custom-scrollbar">
          {liste.map((u, i) => (
            <div key={u.ulke} className="flex items-center gap-4 bg-slate-50 rounded-2xl p-4 border border-slate-100">
              <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest w-6 text-center">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-black text-slate-800 uppercase tracking-tight truncate">{u.ulke}</span>
                  <div className="flex items-center gap-2 ml-2 shrink-0">
                    <span className="bg-indigo-100 text-indigo-700 text-[10px] font-black px-2.5 py-1 rounded-xl uppercase tracking-widest">{u.kisi} kişi</span>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{Math.round((u.kisi / toplam) * 100)}%</span>
                  </div>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5">
                  <div className="bg-gradient-to-r from-indigo-500 to-violet-500 h-1.5 rounded-full transition-all" style={{ width: `${(u.kisi / maxKisi) * 100}%` }} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="px-8 py-5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between shrink-0">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">TOPLAM</span>
          <span className="text-xl font-black text-slate-800 tracking-tighter">{toplam} <span className="text-xs text-slate-400 font-black">MİSAFİR</span></span>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Kara Liste Modal ──────────────────────────────────────────────────────────
function KaraListeModal({ onClose }) {
  const [liste, setListe] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [silOnayId, setSilOnayId] = useState(null);
  const [form, setForm] = useState({ adSoyad: '', tcPasaportNo: '', ulke: 'TÜRKİYE', telefon: '', eklenmaNedeni: '' });
  const setF = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    try { setListe((await apiRequest('istatistik/kara-liste')) || []); } catch { }
    setYukleniyor(false);
  }, []);

  useEffect(() => { yukle(); }, [yukle]);

  async function handleEkle() {
    if (!form.adSoyad.trim()) return;
    setKaydediliyor(true);
    try {
      await apiRequest('istatistik/kara-liste', 'POST', form);
      setForm({ adSoyad: '', tcPasaportNo: '', ulke: 'TÜRKİYE', telefon: '', eklenmaNedeni: '' });
      await yukle();
    } catch { alert('Kayıt başarısız.'); }
    setKaydediliyor(false);
  }

  async function handleSil(id) {
    try { await apiRequest(`istatistik/kara-liste/${id}`, 'DELETE'); setSilOnayId(null); await yukle(); }
    catch { alert('Silme başarısız.'); }
  }

  const rowInp = "w-full border-2 border-gray-100 focus:border-rose-400 rounded-2xl px-4 py-3 text-sm font-bold text-gray-800 bg-white focus:outline-none transition-all shadow-sm";

  return createPortal(
    <div className={MODAL_OVERLAY} onClick={onClose}>
      <div className={`${MODAL_INNER} max-w-3xl max-h-[90vh]`} onClick={e => e.stopPropagation()}>

        <div className="relative flex items-center justify-between px-8 py-7 bg-gradient-to-r from-rose-700 to-red-600 overflow-hidden shrink-0">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-56 h-56 bg-white/10 rounded-full blur-3xl pointer-events-none" />
          <div className="flex items-center gap-4 relative">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-2xl shadow-inner border border-white/30">🚫</div>
            <div>
              <h2 className="font-black text-xl tracking-tight text-white uppercase leading-none">Kara Liste</h2>
              <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-1.5">{liste.length} kayıt</p>
            </div>
          </div>
          <button onClick={onClose} className="relative w-10 h-10 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all text-xl">✕</button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
          {/* Form */}
          <div className="bg-rose-50 border-2 border-rose-100 rounded-[2rem] p-6">
            <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest mb-5">Yeni Kayıt Ekle</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div><label className={LABEL}>Ad Soyad *</label><input value={form.adSoyad} onChange={e => setF('adSoyad', e.target.value)} className={rowInp} placeholder="Ad soyad" /></div>
              <div><label className={LABEL}>T.C / Pasaport</label><input value={form.tcPasaportNo} onChange={e => setF('tcPasaportNo', e.target.value)} className={rowInp} /></div>
              <div>
                <label className={LABEL}>Ülke</label>
                <select value={form.ulke} onChange={e => setF('ulke', e.target.value)} className={rowInp + ' cursor-pointer uppercase'}>
                  {['TÜRKİYE','ALMANYA','İNGİLTERE','RUSYA','FRANSA','ABD','HOLLANDA','DİĞER'].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div><label className={LABEL}>Telefon</label><input value={form.telefon} onChange={e => setF('telefon', e.target.value)} className={rowInp} placeholder="+90 5xx..." /></div>
              <div className="sm:col-span-2">
                <label className={LABEL}>Eklenme Nedeni *</label>
                <input value={form.eklenmaNedeni} onChange={e => setF('eklenmaNedeni', e.target.value)} className={rowInp} placeholder="Örn: Ödeme yapmadı, odaya zarar verdi..." />
              </div>
            </div>
            <button onClick={handleEkle} disabled={kaydediliyor || !form.adSoyad.trim()}
              className="px-8 py-3.5 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 disabled:opacity-50 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-rose-100 transition-all uppercase tracking-widest">
              {kaydediliyor ? '⌛ EKLENİYOR...' : '🚫 KARA LİSTEYE EKLE'}
            </button>
          </div>

          {/* Liste */}
          <div className="rounded-[2rem] overflow-hidden border border-slate-100">
            {yukleniyor ? (
              <div className="text-center py-16 flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-[3px] border-rose-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Yükleniyor...</p>
              </div>
            ) : liste.length === 0 ? (
              <div className="text-center py-16 flex flex-col items-center gap-3 text-slate-400">
                <div className="w-14 h-14 rounded-3xl bg-slate-100 flex items-center justify-center text-3xl">🚫</div>
                <p className="text-[10px] font-black uppercase tracking-widest">Kara listede kayıt bulunmuyor</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[500px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      {['Ad Soyad', 'T.C / Pasaport', 'Ülke', 'Telefon', 'Neden', ''].map(h => (
                        <th key={h} className="text-left py-4 px-5 text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {liste.map(k => (
                      <tr key={k.karaListeId} className="hover:bg-rose-50/30 transition-colors">
                        <td className="py-4 px-5 font-black text-slate-800 text-xs uppercase">{k.adSoyad}</td>
                        <td className="py-4 px-5 font-mono text-slate-500 text-xs">{k.tcPasaportNo || '—'}</td>
                        <td className="py-4 px-5 text-slate-600 text-xs font-bold uppercase">{k.ulke || '—'}</td>
                        <td className="py-4 px-5 text-slate-500 text-xs">{k.telefon || '—'}</td>
                        <td className="py-4 px-5 max-w-[180px]"><span className="text-rose-600 font-bold text-xs">{k.eklenmaNedeni || '—'}</span></td>
                        <td className="py-4 px-5">
                          {silOnayId === k.karaListeId ? (
                            <div className="flex items-center gap-2">
                              <button onClick={() => handleSil(k.karaListeId)} className="bg-rose-600 text-white px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest">Evet</button>
                              <button onClick={() => setSilOnayId(null)} className="bg-slate-100 text-slate-600 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest">İptal</button>
                            </div>
                          ) : (
                            <button onClick={() => setSilOnayId(k.karaListeId)} className="border-2 border-rose-200 text-rose-500 hover:bg-rose-500 hover:text-white hover:border-rose-500 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all">Sil</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Konaklama Geçmişi Modal ───────────────────────────────────────────────────
function KonaklamaDetayModal({ misafir, onClose }) {
  const durumRenk = (d) => {
    if (d === 'C-OUT Yapıldı') return 'bg-emerald-100 text-emerald-700';
    if (d === 'C-IN Yapıldı') return 'bg-sky-100 text-sky-700';
    if (d === 'İptal') return 'bg-red-100 text-red-600';
    return 'bg-amber-100 text-amber-700';
  };

  return createPortal(
    <div className={MODAL_OVERLAY} onClick={onClose}>
      <div className={`${MODAL_INNER} max-w-2xl max-h-[85vh]`} onClick={e => e.stopPropagation()}>
        <div className="relative flex items-center justify-between px-8 py-7 bg-gradient-to-r from-indigo-600 to-violet-600 overflow-hidden shrink-0">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-56 h-56 bg-white/10 rounded-full blur-3xl pointer-events-none" />
          <div className="flex items-center gap-4 relative">
            <InitialsAvatar name={misafir.adSoyad} size="lg" />
            <div>
              <h2 className="font-black text-xl tracking-tight text-white uppercase leading-none">{misafir.adSoyad}</h2>
              <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-1.5">{misafir.konaklamaSayisi} Konaklama Kaydı</p>
            </div>
          </div>
          <button onClick={onClose} className="relative w-10 h-10 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all text-xl">✕</button>
        </div>

        <div className="overflow-y-auto flex-1 custom-scrollbar">
          <div className="rounded-[2rem] overflow-hidden m-6">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['Otel', 'Oda', 'Giriş', 'Çıkış', 'Durum', 'Rez. No'].map(h => (
                    <th key={h} className="text-left py-4 px-5 text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {(misafir.konaklamalar || []).map(k => (
                  <tr key={k.rezervasyonId} className="hover:bg-indigo-50/30 transition-colors">
                    <td className="py-4 px-5 text-slate-600 text-xs font-bold whitespace-nowrap">{k.otelAdi || '—'}</td>
                    <td className="py-4 px-5">
                      <span className="bg-indigo-50 text-indigo-600 border border-indigo-100 px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-widest">{k.odaNo || '—'}</span>
                    </td>
                    <td className="py-4 px-5 text-slate-600 text-xs font-bold whitespace-nowrap">{k.girisTarihi ? fmtTarih(k.girisTarihi) : '—'}</td>
                    <td className="py-4 px-5 text-slate-600 text-xs font-bold whitespace-nowrap">{k.cikisTarihi ? fmtTarih(k.cikisTarihi) : <span className="text-amber-600 text-[10px] font-black uppercase">Belirsiz</span>}</td>
                    <td className="py-4 px-5">
                      <span className={`px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-widest ${durumRenk(k.durum)}`}>{k.durum}</span>
                    </td>
                    <td className="py-4 px-5 font-mono text-slate-400 text-xs">#{k.rezNo || k.rezervasyonId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Ana Sayfa ─────────────────────────────────────────────────────────────────
export default function MisafirListesi() {
  const [aktifModal, setAktifModal] = useState(null); // 'ulke' | 'karaliste'
  const [duzenleM, setDuzenleM] = useState(null);
  const [yeniEkleAcik, setYeniEkleAcik] = useState(false);
  const [silOnay, setSilOnay] = useState(null);
  const [konaklamaDetay, setKonaklamaDetay] = useState(null);
  const [misafirler, setMisafirler] = useState([]);
  const [karaListe, setKaraListe] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);

  const [araAd, setAraAd] = useState('');
  const [araTc, setAraTc] = useState('');
  const [araUlke, setAraUlke] = useState('');
  const [araTel, setAraTel] = useState('');
  const [araFirma, setAraFirma] = useState('');
  const [cinsiyet, setCinsiyet] = useState('Farketm');
  const [aktifSayfa, setAktifSayfa] = useState(1);
  const [sayfaBasi, setSayfaBasi] = useState(100);

  const veriYukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const [m, kl] = await Promise.all([
        apiRequest('misafirler'),
        apiRequest('istatistik/kara-liste'),
      ]);
      setMisafirler(m || []);
      setKaraListe(kl || []);
    } catch { }
    setYukleniyor(false);
  }, []);

  useEffect(() => { veriYukle(); }, [veriYukle]);

  // Kara listede TC/Pasaport eşleşme seti
  const karaListeSet = new Set(
    karaListe.map(k => (k.tcPasaportNo || '').trim()).filter(Boolean)
  );
  const isKaraListe = m => {
    const id = (m.tcPasaportNo || m.pasaportNo || '').trim();
    return id && karaListeSet.has(id);
  };

  const filtrelenmisHepsi = misafirler.filter(m => {
    if (araAd && !m.adSoyad?.toLocaleLowerCase('tr-TR').includes(araAd.toLocaleLowerCase('tr-TR'))) return false;
    if (araTc && !m.tcPasaportNo?.toLowerCase().includes(araTc.toLowerCase())) return false;
    if (araUlke && !m.ulke?.toLocaleLowerCase('tr-TR').includes(araUlke.toLocaleLowerCase('tr-TR'))) return false;
    if (araTel && !m.telefon?.toLowerCase().includes(araTel.toLowerCase())) return false;
    if (araFirma && !m.firma?.toLocaleLowerCase('tr-TR').includes(araFirma.toLocaleLowerCase('tr-TR'))) return false;
    if (cinsiyet !== 'Farketm' && m.cinsiyet !== cinsiyet) return false;
    return true;
  }).sort((a, b) => (a.adSoyad || '').localeCompare(b.adSoyad || '', 'tr'));

  const filtrelenmis = filtrelenmisHepsi.slice((aktifSayfa - 1) * sayfaBasi, aktifSayfa * sayfaBasi);
  const erkekSayisi = misafirler.filter(m => m.cinsiyet === 'Erkek').length;
  const kadinSayisi = misafirler.filter(m => m.cinsiyet === 'Kadın').length;
  const ulkeSayisi = new Set(misafirler.map(m => m.ulke).filter(Boolean)).size;
  const filtreVar = araAd || araTc || araUlke || araTel || araFirma || cinsiyet !== 'Farketm';
  const filtreSifirla = () => { setAraAd(''); setAraTc(''); setAraUlke(''); setAraTel(''); setAraFirma(''); setCinsiyet('Farketm'); setAktifSayfa(1); };

  async function handleSil(misafirId) {
    try { await apiRequest(`misafirler/${misafirId}`, 'DELETE'); setSilOnay(null); await veriYukle(); }
    catch { alert('Silme başarısız.'); }
  }

  const handleMisafirExcelExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('Misafirler', { properties: { tabColor: { argb: 'FF4F81BD' } } });
    
    ws.columns = [
      { header: 'TC / Pasaport', key: 'tc', width: 20 },
      { header: 'Ad', key: 'ad', width: 20 },
      { header: 'Soyad', key: 'soyad', width: 20 },
      { header: 'Firma', key: 'firma', width: 25 },
      { header: 'Cinsiyet', key: 'cinsiyet', width: 15 },
      { header: 'Doğum Tarihi', key: 'dogumTarihi', width: 15 },
      { header: 'Doğum Yeri', key: 'dogumYeri', width: 20 },
      { header: 'Anne Adı', key: 'anaAdi', width: 20 },
      { header: 'Baba Adı', key: 'babaAdi', width: 20 },
      { header: 'Araç Plaka', key: 'plaka', width: 15 },
      { header: 'Telefon', key: 'tel', width: 20 },
      { header: 'E-mail', key: 'email', width: 25 },
      { header: 'Ülke', key: 'ulke', width: 15 },
      { header: 'Uyruk', key: 'uyruk', width: 15 },
      { header: 'Adres', key: 'adres', width: 30 },
      { header: 'Meslek', key: 'meslek', width: 20 },
      { header: 'Konaklamalar', key: 'konaklamalar', width: 45 }
    ];

    ws.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E78' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });
    ws.getRow(1).height = 25;
    
    ws.autoFilter = 'A1:Q1';

    filtrelenmisHepsi.forEach(m => {
      let konaklamaList = (m.konaklamalar || []).map(k => {
          const giris = k.girisTarihi ? fmtTarih(k.girisTarihi) : '?';
          const cikis = k.cikisTarihi ? fmtTarih(k.cikisTarihi) : '?';
          // Açılır listede virgül kullanılamaz, excel ayracı olarak algılar.
          return `Oda ${k.odaNo || '?'} (${giris} - ${cikis}) [${k.durum}]`.replace(/,/g, '');
      });
      if (konaklamaList.length === 0) konaklamaList = ['Konaklama Yok'];
      
      const adSoyadParts = (m.adSoyad || '').trim().split(' ');
      const adVal = adSoyadParts[0] || '';
      const soyadVal = adSoyadParts.length > 1 ? adSoyadParts.slice(1).join(' ') : '';
      const row = ws.addRow({
        tc: m.tcPasaportNo || m.pasaportNo || '',
        ad: adVal,
        soyad: soyadVal,
        firma: m.firma || '',
        cinsiyet: m.cinsiyet || '',
        dogumTarihi: m.dogumTarihi ? fmtTarih(m.dogumTarihi) : '',
        dogumYeri: m.dogumYeri || '',
        anaAdi: m.anaAdi || '',
        babaAdi: m.babaAdi || '',
        plaka: m.aracPlaka || '',
        tel: m.telefon || '',
        email: m.email || '',
        ulke: m.ulke || '',
        uyruk: m.uyruk || '',
        adres: m.adres || '',
        meslek: m.meslek || '',
        konaklamalar: konaklamaList[0]
      });
      
      row.eachCell((cell) => {
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });

      if (konaklamaList.length > 1 || konaklamaList[0] !== 'Konaklama Yok') {
          let listStr = konaklamaList.join(',');
          if (listStr.length > 255) listStr = listStr.substring(0, 250) + '...';
          row.getCell('konaklamalar').dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: [`"${listStr}"`]
          };
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Misafir_Listesi_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleMisafirExcelImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setYukleniyor(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.worksheets[0];

      const yeniMisafirler = [];
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // Header atla

        // A(1): TC/Pasaport, B(2): Ad, C(3): Soyad, D(4): Firma, E(5): Cinsiyet
        // F(6): Doğum Tarihi, G(7): Doğum Yeri, H(8): Anne Adı, I(9): Baba Adı
        // J(10): Araç Plaka, K(11): Telefon, L(12): E-mail, M(13): Ülke
        // N(14): Uyruk, O(15): Adres, P(16): Meslek, Q(17): Konaklamalar
        const tcVal = row.getCell(1).value?.toString()?.trim();
        const adVal = row.getCell(2).value?.toString()?.trim() || '';
        const soyadVal = row.getCell(3).value?.toString()?.trim() || '';
        const adSoyadVal = [adVal, soyadVal].filter(Boolean).join(' ') || null;

        if (adSoyadVal) {
          const m = {
            adSoyad: adSoyadVal,
            tcPasaportNo: tcVal || '',
            firma: row.getCell(4).value?.toString()?.trim() || '',
            cinsiyet: row.getCell(5).value?.toString()?.trim() || '',
            dogumTarihi: null,
            dogumYeri: row.getCell(7).value?.toString()?.trim() || '',
            anaAdi: row.getCell(8).value?.toString()?.trim() || '',
            babaAdi: row.getCell(9).value?.toString()?.trim() || '',
            aracPlaka: row.getCell(10).value?.toString()?.trim() || '',
            telefon: row.getCell(11).value?.toString()?.trim() || '',
            email: row.getCell(12).value?.toString()?.trim() || '',
            ulke: row.getCell(13).value?.toString()?.trim() || '',
            uyruk: row.getCell(14).value?.toString()?.trim() || '',
            adres: row.getCell(15).value?.toString()?.trim() || '',
            meslek: row.getCell(16).value?.toString()?.trim() || '',
            pasaportNo: '',
          };

          // Tarih parse
          const dTarihiStr = row.getCell(6).value?.toString()?.trim();
          if (dTarihiStr) {
            try {
              if (dTarihiStr.includes('.')) {
                const parts = dTarihiStr.split('.');
                m.dogumTarihi = `${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`;
              } else {
                m.dogumTarihi = new Date(dTarihiStr).toISOString();
              }
            } catch { /* sessiz */ }
          }
          yeniMisafirler.push(m);
        }
      });

      if (yeniMisafirler.length === 0) {
        alert("Excel dosyasında eklenecek misafir bulunamadı.");
        setYukleniyor(false);
        return;
      }

      const confirmMsg = `${yeniMisafirler.length} misafir içe aktarılacak. Onaylıyor musunuz?`;
      if (!window.confirm(confirmMsg)) {
        setYukleniyor(false);
        return;
      }

      await apiRequest('misafirler/bulk', 'POST', yeniMisafirler);
      alert('Toplu aktarım başarıyla tamamlandı.');
      veriYukle();
    } catch (e) {
      alert('İçe aktarım sırasında hata oluştu: ' + (e.message || 'Bilinmeyen Hata'));
    }
    setYukleniyor(false);
    e.target.value = null; // Input reset
  };


  // Filtre input stili
  const fi = "w-full bg-slate-50 border-2 border-transparent focus:border-indigo-400 focus:bg-white rounded-2xl px-4 py-2.5 text-xs font-black text-slate-700 uppercase tracking-widest outline-none transition-all placeholder-slate-300";

  return (
    <div className="p-4 sm:p-8 space-y-8 animate-fade-in">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white/95 backdrop-blur-xl rounded-[3rem] p-8 sm:p-10 shadow-2xl border border-white/40 overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-8">

          {/* Başlık */}
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-900 rounded-[2rem] flex items-center justify-center text-white text-3xl shadow-2xl">👤</div>
            <div>
              <h1 className="text-3xl font-black text-slate-800 tracking-tighter uppercase leading-none mb-3">Misafir Kayıtları</h1>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{misafirler.length} kayıtlı misafir</p>
            </div>
          </div>

          {/* Aksiyonlar */}
          <div className="flex flex-wrap justify-start sm:justify-end items-center gap-3 w-full lg:w-auto">
            <button onClick={() => setAktifModal('ulke')}
              className="h-12 px-5 bg-indigo-50 text-indigo-600 border-2 border-indigo-100 hover:border-indigo-400 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all">
              🌍 Ülke İstatistiği
            </button>
            <button onClick={() => setAktifModal('karaliste')}
              className="h-12 px-5 bg-rose-50 text-rose-600 border-2 border-rose-100 hover:border-rose-400 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all">
              🚫 Kara Liste
            </button>
            <label className="h-12 px-5 bg-teal-50 text-teal-700 border-2 border-teal-100 hover:border-teal-400 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center justify-center cursor-pointer gap-2">
              📥 İçe Aktar
              <input type="file" accept=".xlsx, .xls" className="hidden" onChange={handleMisafirExcelImport} />
            </label>
            <button
              onClick={handleMisafirExcelExport}
              className="h-12 px-5 bg-emerald-50 text-emerald-700 border-2 border-emerald-100 hover:border-emerald-400 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all">
              📊 Dışa Aktar
            </button>
            <button onClick={() => setYeniEkleAcik(true)}
              className="h-12 px-8 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-indigo-100 active:scale-95 transition-all">
              + YENİ MİSAFİR
            </button>
            <button onClick={veriYukle}
              className="w-12 h-12 bg-white border-2 border-slate-100 hover:border-indigo-400 rounded-2xl flex items-center justify-center shadow-lg transition-all text-slate-500 text-lg">
              ↺
            </button>
          </div>
        </div>

        {/* Stat Kartları */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
          {[
            { label: 'Toplam Misafir', v: misafirler.length, c: 'from-slate-700 to-slate-900', i: '👤' },
            { label: 'Erkek', v: erkekSayisi, c: 'from-sky-500 to-indigo-600', i: '♂' },
            { label: 'Kadın', v: kadinSayisi, c: 'from-rose-400 to-pink-600', i: '♀' },
            { label: 'Ülke Sayısı', v: ulkeSayisi, c: 'from-emerald-400 to-teal-600', i: '🌍' },
          ].map(s => (
            <div key={s.label} className={`bg-gradient-to-br ${s.c} p-5 rounded-[2rem] text-white shadow-xl relative overflow-hidden group transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:-translate-y-1 cursor-default`}>
              <div className="absolute top-0 right-0 -mr-8 -mt-8 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all duration-500" />
              <span className="absolute top-2 right-3 opacity-20 text-3xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">{s.i}</span>
              <p className="text-[8px] font-black opacity-60 uppercase tracking-tight mb-1 relative z-10 pr-8 truncate">{s.label}</p>
              <h3 className="text-2xl sm:text-3xl font-black tracking-tighter relative z-10">{s.v}</h3>
            </div>
          ))}
        </div>
      </div>

      {/* ── Tablo Kartı ────────────────────────────────────────────────────── */}
      <div className="bg-white/95 backdrop-blur-xl rounded-[3rem] shadow-2xl border border-white/40 overflow-hidden">

        {/* Tablo Başlığı */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-8 py-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-black text-slate-700 uppercase tracking-widest">{filtrelenmisHepsi.length} Kayıt</h2>
            {filtreVar && (
              <span className="bg-indigo-100 text-indigo-700 text-[9px] font-black px-3 py-1 rounded-xl uppercase tracking-widest">Filtre Aktif</span>
            )}
            {filtreVar && (
              <button onClick={filtreSifirla} className="text-[9px] font-black text-rose-500 hover:text-rose-700 uppercase tracking-widest transition-colors">✕ Temizle</button>
            )}
          </div>
        </div>

        {/* Mobile Arama */}
        <div className="block sm:hidden px-6 py-4 border-b border-slate-100 space-y-3">
          <input value={araAd} onChange={e => { setAraAd(e.target.value); setAktifSayfa(1); }} placeholder="Ad Soyad ara..." className={fi} />
          <div className="grid grid-cols-2 gap-3">
            <input value={araTc} onChange={e => { setAraTc(e.target.value); setAktifSayfa(1); }} placeholder="TC / Pasaport..." className={fi} />
            <input value={araUlke} onChange={e => { setAraUlke(e.target.value); setAktifSayfa(1); }} placeholder="Ülke..." className={fi} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input value={araTel} onChange={e => { setAraTel(e.target.value); setAktifSayfa(1); }} placeholder="Telefon..." className={fi} />
            <select value={cinsiyet} onChange={e => { setCinsiyet(e.target.value); setAktifSayfa(1); }} className={fi + ' cursor-pointer'}>
              <option value="Farketm">Tüm Cinsiyetler</option>
              <option value="Erkek">Erkek</option>
              <option value="Kadın">Kadın</option>
            </select>
          </div>
        </div>

        {yukleniyor ? (
          <LoadingSpinner fullPage text="Misafirler yükleniyor..." color="indigo" size="lg" />
        ) : (
          <>
            {/* ── Mobile Kart Listesi ── */}
            <div className="block sm:hidden divide-y divide-slate-50">
              {filtrelenmis.length === 0 ? (
                <div className="text-center py-24 flex flex-col items-center gap-4">
                  <div className="w-16 h-16 rounded-3xl bg-slate-100 flex items-center justify-center text-3xl">👤</div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Misafir kaydı bulunamadı</p>
                </div>
              ) : filtrelenmis.map(m => (
                <div key={m.misafirId} className={`px-6 py-5 ${isKaraListe(m) ? 'bg-rose-50/40' : ''}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <InitialsAvatar name={m.adSoyad} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-black text-slate-800 text-sm uppercase tracking-tight truncate">{m.adSoyad || '—'}</p>
                          {isKaraListe(m) && (
                            <span className="inline-flex items-center gap-1 bg-rose-100 border border-rose-200 text-rose-700 px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest shrink-0">
                              🚫 Kara Liste
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mt-0.5">{m.tcPasaportNo || m.pasaportNo || '—'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setDuzenleM(m)}
                        className="bg-indigo-600 border-2 border-indigo-600 text-white text-[10px] px-4 py-1.5 rounded-xl font-black uppercase tracking-widest transition-all active:scale-95">
                        Düzenle
                      </button>
                      <button onClick={() => setSilOnay({ misafirId: m.misafirId, adSoyad: m.adSoyad })}
                        className="border-2 border-rose-200 text-rose-500 hover:bg-rose-500 hover:text-white text-[10px] px-3 py-1.5 rounded-xl font-black uppercase tracking-widest transition-all">
                        Sil
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 pl-12">
                    <span className={`px-2.5 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest ${m.cinsiyet === 'Erkek' ? 'bg-sky-50 text-sky-700' : 'bg-rose-50 text-rose-600'}`}>{m.cinsiyet || '—'}</span>
                    <span className="bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest">{m.ulke || '—'}</span>
                    {yasHesapla(m.dogumTarihi) != null && (
                      <span className="bg-amber-50 text-amber-700 px-2.5 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest">{yasHesapla(m.dogumTarihi)} YAŞ</span>
                    )}
                    {m.telefon && <span className="text-slate-400 text-[10px] font-bold self-center">{m.telefon}</span>}
                    {m.konaklamaSayisi > 0 && (
                      <button onClick={() => setKonaklamaDetay(m)}
                        className="bg-slate-100 hover:bg-indigo-100 text-slate-600 hover:text-indigo-700 px-2.5 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all">
                        🏨 {m.konaklamaSayisi} Konaklama
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Desktop Tablo ── */}
            <div className="hidden sm:block overflow-x-auto">
              <table style={{ minWidth: '1650px', width: '100%' }}>
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-100">
                    {['#', 'Ad Soyad', 'TC / Pasaport', 'Yaş', 'Doğum Tarihi', 'Doğum Yeri', 'Ülke', 'Telefon', 'E-mail', 'Cinsiyet', 'Meslek', 'Firma', 'Adres', 'Konaklama', 'Kayıt Tarihi', 'İşlemler'].map(h => (
                      <th key={h} className="text-left py-4 px-4 text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                  {/* Filtre Satırı */}
                  <tr className="bg-slate-50/50 border-b border-slate-100">
                    <td className="px-3 py-2" />
                    <td className="px-3 py-2"><input value={araAd} onChange={e => { setAraAd(e.target.value); setAktifSayfa(1); }} placeholder="Ad Soyad..." className={fi} /></td>
                    <td className="px-3 py-2"><input value={araTc} onChange={e => { setAraTc(e.target.value); setAktifSayfa(1); }} placeholder="TC / Pasaport..." className={fi} /></td>
                    <td className="px-3 py-2" /><td className="px-3 py-2" /><td className="px-3 py-2" />
                    <td className="px-3 py-2"><input value={araUlke} onChange={e => { setAraUlke(e.target.value); setAktifSayfa(1); }} placeholder="Ülke..." className={fi} /></td>
                    <td className="px-3 py-2"><input value={araTel} onChange={e => { setAraTel(e.target.value); setAktifSayfa(1); }} placeholder="Telefon..." className={fi} /></td>
                    <td className="px-3 py-2" />
                    <td className="px-3 py-2">
                      <select value={cinsiyet} onChange={e => { setCinsiyet(e.target.value); setAktifSayfa(1); }} className={fi + ' cursor-pointer'}>
                        <option value="Farketm">Tümü</option>
                        <option value="Erkek">Erkek</option>
                        <option value="Kadın">Kadın</option>
                      </select>
                    </td>
                    <td className="px-3 py-2"><input value={araFirma} onChange={e => { setAraFirma(e.target.value); setAktifSayfa(1); }} placeholder="Firma..." className={fi} /></td>
                    <td className="px-3 py-2" /><td className="px-3 py-2" /><td className="px-3 py-2" /><td className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filtrelenmis.length === 0 ? (
                    <tr>
                      <td colSpan={16} className="text-center py-24">
                        <div className="flex flex-col items-center gap-4 text-slate-400">
                          <div className="w-16 h-16 rounded-3xl bg-slate-100 flex items-center justify-center text-3xl">👤</div>
                          <p className="text-[10px] font-black uppercase tracking-widest">Misafir kaydı bulunamadı</p>
                        </div>
                      </td>
                    </tr>
                  ) : filtrelenmis.map((m, i) => (
                    <tr key={m.misafirId} className={`hover:bg-indigo-50/30 transition-colors group ${isKaraListe(m) ? 'bg-rose-50/40' : ''}`}>
                      <td className="py-4 px-4 text-[10px] font-black text-slate-300 tracking-widest">{(aktifSayfa - 1) * sayfaBasi + i + 1}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <InitialsAvatar name={m.adSoyad} />
                          <div className="flex flex-col gap-1">
                            <span className="font-black text-slate-800 text-xs uppercase tracking-tight whitespace-nowrap">{m.adSoyad || '—'}</span>
                            {isKaraListe(m) && (
                              <span className="inline-flex items-center gap-1 bg-rose-100 border border-rose-200 text-rose-700 px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest w-fit">
                                🚫 Kara Liste
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 font-mono text-slate-500 text-xs">{m.tcPasaportNo || m.pasaportNo || '—'}</td>
                      <td className="py-4 px-4 text-center">
                        {yasHesapla(m.dogumTarihi) != null
                          ? <span className="bg-amber-50 text-amber-700 px-2.5 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest">{yasHesapla(m.dogumTarihi)}</span>
                          : <span className="text-slate-300 text-xs font-black">—</span>}
                      </td>
                      <td className="py-4 px-4 text-slate-500 text-xs font-bold whitespace-nowrap">{m.dogumTarihi ? fmtTarih(m.dogumTarihi) : '—'}</td>
                      <td className="py-4 px-4 text-slate-500 text-xs font-bold">{m.dogumYeri || '—'}</td>
                      <td className="py-4 px-4">
                        <span className="bg-indigo-50 text-indigo-700 border border-indigo-100 px-2.5 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest whitespace-nowrap">{m.ulke || '—'}</span>
                      </td>
                      <td className="py-4 px-4 text-slate-600 text-xs font-bold whitespace-nowrap">{m.telefon || '—'}</td>
                      <td className="py-4 px-4 text-slate-500 text-xs">{m.email || '—'}</td>
                      <td className="py-4 px-4">
                        <span className={`px-2.5 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest ${m.cinsiyet === 'Erkek' ? 'bg-sky-50 text-sky-700' : m.cinsiyet === 'Kadın' ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-500'}`}>
                          {m.cinsiyet || '—'}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-slate-500 text-xs font-bold">{m.meslek || '—'}</td>
                      <td className="py-4 px-4 whitespace-nowrap">
                        {m.firma
                          ? <span className="bg-violet-50 text-violet-700 border border-violet-100 px-2.5 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest">{m.firma}</span>
                          : <span className="text-slate-300 text-xs font-black">—</span>
                        }
                      </td>
                      <td className="py-4 px-4 text-slate-400 text-xs font-bold max-w-[120px] truncate" title={m.adres}>{m.adres || '—'}</td>
                      <td className="py-4 px-4">
                        {m.konaklamaSayisi > 0 ? (
                          <button onClick={() => setKonaklamaDetay(m)}
                            className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-100 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all whitespace-nowrap">
                            🏨 {m.konaklamaSayisi}
                          </button>
                        ) : <span className="text-slate-200 text-xs font-black">—</span>}
                      </td>
                      <td className="py-4 px-4 text-slate-400 text-xs font-bold whitespace-nowrap">{m.olusturmaTarihi ? fmtTarih(m.olusturmaTarihi) : '—'}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setDuzenleM(m)}
                            className="bg-slate-100 border-2 border-transparent hover:bg-indigo-600 hover:text-white text-slate-600 text-[9px] px-4 py-1.5 rounded-xl font-black uppercase tracking-widest transition-all group-hover:bg-indigo-600 group-hover:text-white">
                            Düzenle
                          </button>
                          <button onClick={() => setSilOnay({ misafirId: m.misafirId, adSoyad: m.adSoyad })}
                            className="border-2 border-rose-200 text-rose-500 hover:bg-rose-500 hover:text-white hover:border-rose-500 text-[9px] px-3 py-1.5 rounded-xl font-black uppercase tracking-widest transition-all">
                            Sil
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <Pagination
          toplam={filtrelenmisHepsi.length}
          sayfaBasi={sayfaBasi}
          sayfaBasiDegistir={setSayfaBasi}
          aktifSayfa={aktifSayfa}
          sayfaDegistir={setAktifSayfa}
        />
      </div>

      {/* ── Sil Onay ────────────────────────────────────────────────────────── */}
      {silOnay && createPortal(
        <div className={MODAL_OVERLAY} onClick={() => setSilOnay(null)}>
          <div className={`${MODAL_INNER} max-w-md`} onClick={e => e.stopPropagation()}>
            <div className="relative px-8 py-7 bg-gradient-to-r from-rose-600 to-red-600 overflow-hidden">
              <div className="absolute top-0 right-0 -mr-16 -mt-16 w-56 h-56 bg-white/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-2xl shadow-inner border border-white/30">🗑️</div>
                <div>
                  <h2 className="font-black text-xl tracking-tight text-white uppercase leading-none">Misafiri Sil</h2>
                  <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-1.5">Bu işlem geri alınamaz</p>
                </div>
              </div>
            </div>
            <div className="px-8 py-8">
              <p className="text-sm font-bold text-slate-600 mb-2">Aşağıdaki misafiri silmek istediğinize emin misiniz?</p>
              <p className="text-lg font-black text-slate-800 uppercase tracking-tight">{silOnay.adSoyad}</p>
            </div>
            <div className="px-8 py-6 border-t border-gray-100 bg-gray-50/50 flex gap-3 justify-end">
              <button onClick={() => setSilOnay(null)} className="px-7 py-3.5 rounded-2xl border-2 border-gray-100 text-[10px] font-black text-gray-400 hover:bg-gray-100 transition-all uppercase tracking-widest">
                İPTAL
              </button>
              <button onClick={() => handleSil(silOnay.misafirId)}
                className="px-8 py-3.5 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-rose-100 transition-all uppercase tracking-widest active:scale-95">
                🗑️ SİL
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Modaller ──────────────────────────────────────────────────────── */}
      {aktifModal === 'ulke' && <UlkeIstatistikModal misafirler={misafirler} onClose={() => setAktifModal(null)} />}
      {aktifModal === 'karaliste' && <KaraListeModal onClose={() => { setAktifModal(null); veriYukle(); }} />}
      {duzenleM && <MisafirDuzenleModal misafir={duzenleM} onClose={() => setDuzenleM(null)} onKaydedildi={veriYukle} />}
      {yeniEkleAcik && <MisafirDuzenleModal misafir={{}} onClose={() => setYeniEkleAcik(false)} onKaydedildi={veriYukle} yeniKayit />}
      {konaklamaDetay && <KonaklamaDetayModal misafir={konaklamaDetay} onClose={() => setKonaklamaDetay(null)} />}

      {/* ── Alt Bilgi ──────────────────────────────────────────────────────── */}
      <UsageInfo
        id="misafir-liste-yardim"
        color="indigo"
        title="Nasıl Kullanılır?"
        description="Misafir kayıtları sayfasında tüm konaklamış ve kayıtlı misafirlerin detaylı bilgilerini yönetebilir, yeni kayıt oluşturabilir veya mevcut kayıtları düzenleyebilirsiniz."
        scenarios={[
          { title: "Misafir Düzenleme & Silme", content: "Tablodaki misafirlerin yanındaki butonları kullanarak bilgilerini güncelleyebilir veya gerekiyorsa kaydı silebilirsiniz." },
          { title: "Konaklama Geçmişi", content: "Misafirin yanındaki Otel ikonuna (🏨) tıklayarak o misafirin daha önceki tüm konaklamalarını ve durumlarını görebilirsiniz." },
          { title: "Kara Liste Kontrolü", content: "Sorunlu misafirleri 'Kara Liste' butonuna tıklayarak ekleyebilir, bu sayede rezervasyon sırasında uyarı alınmasını sağlayabilirsiniz." }
        ]}
      />
    </div>
  );
}
